// Omnix/client/ui/ContactIndexView.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class ContactIndexView extends ComponentBase {
    static styles = `
        .contact-index-view { display: flex; flex-direction: column; height: 100%; }
        .index-header { padding: 0.75rem 1rem; border-bottom: 1px solid var(--bs-border-color); flex-shrink: 0; }
        .index-search { padding: 0.5rem 1rem; border-bottom: 1px solid var(--bs-border-color); flex-shrink: 0; }
        .contact-list { flex-grow: 1; overflow-y: auto; }
        .contact-list-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 1rem; cursor: pointer; border-bottom: 1px solid var(--bs-border-color-translucent); }
        .contact-list-item:hover { background-color: var(--bs-tertiary-bg); }
        .contact-list-item.active { background-color: var(--bs-primary-bg-subtle); font-weight: 500; }
        .contact-info { overflow: hidden; }
        .contact-name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .contact-subtext { font-size: 0.8rem; color: var(--bs-secondary-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    `;

    constructor(config) {
        super(config);
        this.allContacts = [];
        this.filteredContacts = [];
        this.selectedContactId = null;
        this._debouncedSearch = this.debounce(this.filterContacts.bind(this), 250);
        this.applyStyles();
    }

    setContacts(contacts) {
        this.allContacts = contacts;
        this.filterContacts(this.searchInput?.value || '');
    }

    filterContacts(searchTerm) {
        const lowerTerm = searchTerm.toLowerCase();
        if (!lowerTerm) {
            this.filteredContacts = [...this.allContacts];
        } else {
            this.filteredContacts = this.allContacts.filter(c =>
                `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(lowerTerm) ||
                (c.email || '').toLowerCase().includes(lowerTerm) ||
                (c.company || '').toLowerCase().includes(lowerTerm)
            );
        }
        this.renderContactList();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'contact-index-view';
            this.container.innerHTML = `
                <div class="index-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0" data-i18n="Contacts">Contacts</h5>
                    <div class="btn-group btn-group-sm gap-2">
                        <button class="btn btn-outline-secondary" id="refresh-contacts-btn" title="Refresh Contacts"><i class="bi bi-arrow-clockwise"></i></button>
                        <button class="btn btn-primary" id="new-contact-btn" title="New Contact"><i class="bi bi-plus-lg"></i></button>
                    </div>
                </div>
                <div class="index-search">
                    <input type="search" class="form-control form-control-sm" placeholder="Search contacts...">
                </div>
                <div class="contact-list list-group list-group-flush"></div>
            `;
            this.searchInput = this.container.querySelector('input[type="search"]');
            this.contactListContainer = this.container.querySelector('.contact-list');

            this.container.querySelector('#new-contact-btn').addEventListener('click', () => this.emit('newContactClicked'));

            this.container.querySelector('#refresh-contacts-btn').addEventListener('click', () => this.emit('refreshContacts'));

            this.searchInput.addEventListener('input', (e) => this._debouncedSearch(e.target.value));
        }
        this.renderContactList();
        return this.container;
    }

    renderContactList() {
        this.contactListContainer.innerHTML = '';
        if (this.filteredContacts.length === 0) {
            this.contactListContainer.innerHTML = `<div class="p-3 text-center text-muted">${this.t('No contacts found.')}</div>`;
            return;
        }

        this.filteredContacts.forEach(contact => {
            const item = document.createElement('div');
            item.className = 'contact-list-item';
            item.dataset.contactId = contact._id;
            if (contact._id === this.selectedContactId) {
                item.classList.add('active');
            }

            const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || 'Unnamed Contact';
            const avatar = createAvatarElement({ displayName, sizeClass: 'default' });

            item.innerHTML = `
                ${avatar.outerHTML}
                <div class="contact-info">
                    <div class="contact-name">${displayName}</div>
                    <div class="contact-subtext">${contact.email || contact.company || ''}</div>
                </div>
            `;
            item.addEventListener('click', () => {
                this.selectedContactId = contact._id;
                this.container.querySelectorAll('.contact-list-item').forEach(el => el.classList.remove('active'));
                item.classList.add('active');
                this.emit('contactSelected', contact);
            });
            this.contactListContainer.appendChild(item);
        });
    }

    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }
}